"use client"

import { useMemo, useState, type ReactNode } from "react"
import {
  SectionRule,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { readQuantity } from "@/lib/tools/numeric-input"

// ---------- Calc logic ----------
// Generic head-to-head spec comparator: purchase price + annual kWh + capacity.
//
// Energy-cost framing uses a 16.5¢/kWh default (US average per EIA) and a
// 10-year horizon — consistent with ENERGY STAR's appliance lifetime
// assumption for refrigerators. The user can edit the rate.

interface Comparison {
  feature: string
  a: string
  b: string
  winner: "a" | "b" | "tie"
}

interface Results {
  comparison: Comparison[]
  scoreA: number
  scoreB: number
  totalA: number
  totalB: number
  diff: number
  winner: "a" | "b" | "tie"
}

function computeResults(
  p1: number | null,
  p2: number | null,
  e1: number | null,
  e2: number | null,
  c1: number | null,
  c2: number | null,
  rate: number | null,
): Results | null {
  // `=== null`, not `!` — see the reader note at the call site below.
  if (
    p1 === null ||
    p2 === null ||
    e1 === null ||
    e2 === null ||
    c1 === null ||
    c2 === null ||
    rate === null
  ) {
    return null
  }

  const comparison: Comparison[] = []
  let scoreA = 0
  let scoreB = 0

  // Price
  comparison.push({
    feature: "Purchase price",
    a: `$${p1.toLocaleString()}`,
    b: `$${p2.toLocaleString()}`,
    winner: p1 < p2 ? "a" : p2 < p1 ? "b" : "tie",
  })
  if (p1 < p2) scoreA++
  else if (p2 < p1) scoreB++

  // Energy
  comparison.push({
    feature: "Annual energy use",
    a: `${e1} kWh/yr`,
    b: `${e2} kWh/yr`,
    winner: e1 < e2 ? "a" : e2 < e1 ? "b" : "tie",
  })
  if (e1 < e2) scoreA++
  else if (e2 < e1) scoreB++

  // Capacity
  comparison.push({
    feature: "Total capacity",
    a: `${c1} cu ft`,
    b: `${c2} cu ft`,
    winner: c1 > c2 ? "a" : c2 > c1 ? "b" : "tie",
  })
  if (c1 > c2) scoreA++
  else if (c2 > c1) scoreB++

  // Cost per cu ft
  const cpcf1 = p1 / c1
  const cpcf2 = p2 / c2
  comparison.push({
    feature: "Cost per cu ft",
    a: `$${cpcf1.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    b: `$${cpcf2.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    winner: cpcf1 < cpcf2 ? "a" : cpcf2 < cpcf1 ? "b" : "tie",
  })
  if (cpcf1 < cpcf2) scoreA++
  else if (cpcf2 < cpcf1) scoreB++

  // 10-year energy cost using user rate
  const energyCost1 = (e1 * (rate / 100)) * 10
  const energyCost2 = (e2 * (rate / 100)) * 10
  comparison.push({
    feature: "10-yr energy cost",
    a: `$${energyCost1.toLocaleString("en-US", { maximumFractionDigits: 0 })}`,
    b: `$${energyCost2.toLocaleString("en-US", { maximumFractionDigits: 0 })}`,
    winner: energyCost1 < energyCost2 ? "a" : energyCost2 < energyCost1 ? "b" : "tie",
  })
  if (energyCost1 < energyCost2) scoreA++
  else if (energyCost2 < energyCost1) scoreB++

  // 10-year total cost (weighted)
  const totalA = p1 + energyCost1
  const totalB = p2 + energyCost2
  comparison.push({
    feature: "10-yr total cost",
    a: `$${totalA.toLocaleString("en-US", { maximumFractionDigits: 0 })}`,
    b: `$${totalB.toLocaleString("en-US", { maximumFractionDigits: 0 })}`,
    winner: totalA < totalB ? "a" : totalB < totalA ? "b" : "tie",
  })
  if (totalA < totalB) scoreA += 2
  else if (totalB < totalA) scoreB += 2

  const winner: "a" | "b" | "tie" = scoreA > scoreB ? "a" : scoreB > scoreA ? "b" : "tie"

  return {
    comparison,
    scoreA,
    scoreB,
    totalA,
    totalB,
    diff: Math.abs(totalA - totalB),
    winner,
  }
}

// ---------- Sides ----------

interface Side {
  key: "a" | "b"
  name: string
  tag: string
  color: string
  pros: string[]
  cons: string[]
}

const SIDE_BUDGET: Side = {
  key: "a",
  name: "Lower-priced model",
  tag: "Lower upfront cost",
  color: "#0469f0",
  pros: [
    "Less cash out the door",
    "Easier to absorb if it fails outside warranty",
    "Often simpler — fewer electronic failure points",
  ],
  cons: [
    "Often higher kWh — pays back over 10 yrs of operation",
    "Sometimes lower capacity per dollar over the long run",
    "Shorter warranty terms common at the entry tier",
  ],
}

const SIDE_PREMIUM: Side = {
  key: "b",
  name: "Higher-priced model",
  tag: "Lower lifetime cost (often)",
  color: "#ff9951",
  pros: [
    "Lower kWh — pays itself back over a 10-yr life",
    "Larger usable capacity at most price points",
    "Longer sealed-system warranty terms common",
  ],
  cons: [
    "More cash up front",
    "More complex electronics — more eventual failure points",
    "Loss-of-value if you replace before payoff window",
  ],
}

interface Scenario {
  who: string
  winner: "a" | "b"
  reason: string
}

const SCENARIOS: Scenario[] = [
  {
    who: "Renting and moving in 2 years",
    winner: "a",
    reason: "Lifetime energy savings haven't paid back — minimize cash out the door.",
  },
  {
    who: "Forever home, 10+ year horizon",
    winner: "b",
    reason: "Higher-efficiency model recovers its premium in lower kWh over a typical fridge lifespan.",
  },
  {
    who: "Cash-tight household",
    winner: "a",
    reason: "Energy savings are real but they're spread over years. Cash today often beats $30/yr of savings.",
  },
  {
    who: "Energy-cost focused (15¢+/kWh)",
    winner: "b",
    reason: "At above-average electricity rates, the 10-yr energy gap on a low-efficiency model is large.",
  },
  {
    who: "Family of 5+",
    winner: "b",
    reason: "Higher capacity per dollar usually lives at the upper tiers — less wasted-trip grocery shopping.",
  },
  {
    who: "Single or couple",
    winner: "a",
    reason: "Lower capacity is fine; the premium-tier feature set rarely earns its keep.",
  },
]

interface Field {
  label: string
  hint?: string
  el: ReactNode
}

function InputField({ label, hint, el }: Field) {
  return (
    <div>
      <div className="mb-2">
        <label className="block text-[12px] font-bold uppercase tracking-[0.1em] text-gray-900">{label}</label>
        {hint && <span className="block text-[10px] text-gray-500 mt-0.5">{hint}</span>}
      </div>
      {el}
    </div>
  )
}

// ---------- Page ----------

export function ApplianceComparisonToolPageClient() {
  const [price1, setPrice1] = useState("1200")
  const [price2, setPrice2] = useState("1500")
  const [energy1, setEnergy1] = useState("520")
  const [energy2, setEnergy2] = useState("420")
  const [capacity1, setCapacity1] = useState("21")
  const [capacity2, setCapacity2] = useState("23")
  const [rate, setRate] = useState("16.5")
  const [tab, setTab] = useState<"french" | "counter">("french")

  // Bounds unchanged — they are the ones this tool already declared. What
  // changes is that out-of-range input is now REJECTED rather than CLAMPED.
  // `safeParseNumber(price1, 100, 50000)` returned `100` for a typed `-1200`,
  // and `100` is truthy, so `computeResults`' guard could not fire: the tool
  // scored a side-by-side comparison against prices the visitor never entered.
  // FRIDGE-384 (the guard) / FRIDGE-411 (the clamp).
  const p1 = readQuantity(price1, { min: 100, max: 50000 })
  const p2 = readQuantity(price2, { min: 100, max: 50000 })
  const e1 = readQuantity(energy1, { min: 100, max: 2000 })
  const e2 = readQuantity(energy2, { min: 100, max: 2000 })
  const c1 = readQuantity(capacity1, { min: 1, max: 50 })
  const c2 = readQuantity(capacity2, { min: 1, max: 50 })
  const rateNum = readQuantity(rate, { min: 1, max: 100 })

  const results = useMemo(
    () => computeResults(p1, p2, e1, e2, c1, c2, rateNum),
    [p1, p2, e1, e2, c1, c2, rateNum],
  )

  return (
    <ToolPageShell
      toolName="Refrigerator Comparison Tool"
      heroEyebrow="Comparators"
      heroTitlePre="Two fridges,"
      heroTitleItalic="one"
      heroTitlePost="honest verdict."
      heroSubtitle="Drop in price, annual kWh, and capacity for each unit. We'll roll it up into 10-year total cost — the only number that actually matters when you're choosing between two refrigerators."
      heroMeta={["⏱ ~2 min read", "EIA rate baseline + ENERGY STAR 10-yr lifetime"]}
      heroShare={<ToolShareBar title="Refrigerator Comparison Tool" />}
      heroRight={
        <div className="rounded-xl p-6 bg-gray-50 border border-gray-200">
          <div className="flex items-start gap-3">
            <div
              className="font-serif text-[#ff9951] leading-none flex-shrink-0"
              style={{ fontSize: 40, letterSpacing: "-0.05em" }}
            >
              &ldquo;
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] font-bold mb-2 text-[#ff9951]">
                The short answer
              </div>
              <p
                className="font-medium text-gray-900"
                style={{
                  fontSize: 16,
                  lineHeight: 1.5,
                  letterSpacing: "-0.01em",
                  textWrap: "pretty" as never,
                }}
              >
                Compare on{" "}
                <em className="italic text-[#ff9951] font-bold">10-year total cost</em>, not
                sticker price. A $300 cheaper fridge that uses 100 more kWh/yr costs you{" "}
                <em className="italic text-[#ff9951] font-bold">more</em> over its life.
              </p>
            </div>
          </div>
        </div>
      }
      faqs={[
        {
          question: "Why 10 years?",
          answer:
            "ENERGY STAR uses a 12-year average refrigerator lifespan in its savings calculations; we use a slightly more conservative 10-year horizon for the lifetime energy comparison. The actual figure depends on usage and maintenance.",
        },
        {
          question: "Where do I find each fridge's annual kWh?",
          answer:
            "Look for the yellow EnergyGuide label on the unit, on the manufacturer's spec page, or in the ENERGY STAR product database. The number is required by FTC rule for full-size refrigerators.",
        },
        {
          question: "What rate should I use?",
          answer:
            "Default is 16.5¢/kWh, the recent US residential average per EIA. Your local rate could be 10¢ in low-cost markets or 30¢+ in CA/HI/MA. Check your most recent utility bill.",
        },
        {
          question: "Does the 10-yr cost include repairs?",
          answer:
            "No — only purchase price plus 10-year energy cost. Repair frequency varies wildly by brand and ice/water dispenser presence; treat that as a separate decision input.",
        },
        {
          question: "What about capacity per dollar?",
          answer:
            "We surface cost per cu ft as a separate row. Capacity scales sub-linearly with price — bigger fridges usually cost more per cu ft, not less, until you reach the very top tier.",
        },
      ]}
      relatedTools={[
        { title: "Energy Cost Calculator", category: "Energy & Cost", description: "Annual cost for any appliance.", href: "/tools/energy-cost-calculator", time: "~30 sec" },
        { title: "Refrigerator Size Calculator", category: "Size & Capacity", description: "Right cu ft for your household.", href: "/tools/refrigerator-size-calculator", time: "~45 sec" },
        { title: "Find Your Fridge", category: "Quiz", description: "Five questions to a starting product group.", href: "/tools/find-your-fridge", time: "~90 sec" },
        { title: "French Door vs Side-by-Side", category: "Comparison", description: "Door style head-to-head.", href: "/tools/french-door-vs-side-by-side-refrigerator", time: "~3 min" },
      ]}
      cta={{
        title: "Ready to shop a winning fridge?",
        body: "Browse French door refrigerators across every major retailer.",
        buttonLabel: "Browse French door fridges",
        buttonHref: "/french-door-refrigerators",
      }}
    >
      {/* ============ COMPARISON CALCULATOR ============ */}
      <section className="py-8 md:py-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid lg:grid-cols-[460px_1fr] gap-8 lg:gap-12">
          <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 lg:sticky lg:top-24 lg:self-start">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-5 text-[#ff9951]">
              Drop in two fridges
            </div>
            <div className="space-y-6">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#0469f0]">Fridge A</div>
                <div className="grid grid-cols-2 gap-3">
                  <InputField
                    label="Price ($)"
                    el={
                      <input type="number" min={100} max={50000} value={price1} onChange={(e) => setPrice1(e.target.value)}
                        className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold" />
                    }
                  />
                  <InputField
                    label="kWh/yr"
                    el={
                      <input type="number" min={100} max={2000} value={energy1} onChange={(e) => setEnergy1(e.target.value)}
                        className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold" />
                    }
                  />
                </div>
                <div className="mt-3">
                  <InputField
                    label="Capacity (cu ft)"
                    el={
                      <input type="number" min={1} max={50} value={capacity1} onChange={(e) => setCapacity1(e.target.value)}
                        className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold" />
                    }
                  />
                </div>
              </div>

              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Fridge B</div>
                <div className="grid grid-cols-2 gap-3">
                  <InputField
                    label="Price ($)"
                    el={
                      <input type="number" min={100} max={50000} value={price2} onChange={(e) => setPrice2(e.target.value)}
                        className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold" />
                    }
                  />
                  <InputField
                    label="kWh/yr"
                    el={
                      <input type="number" min={100} max={2000} value={energy2} onChange={(e) => setEnergy2(e.target.value)}
                        className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold" />
                    }
                  />
                </div>
                <div className="mt-3">
                  <InputField
                    label="Capacity (cu ft)"
                    el={
                      <input type="number" min={1} max={50} value={capacity2} onChange={(e) => setCapacity2(e.target.value)}
                        className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold" />
                    }
                  />
                </div>
              </div>

              <InputField
                label="Electricity rate (¢/kWh)"
                hint="US average is 16.5¢/kWh (EIA)"
                el={
                  <input type="number" min={1} max={100} step={0.1} value={rate} onChange={(e) => setRate(e.target.value)}
                    className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold" />
                }
              />
            </div>
          </div>

          <div>
            {results && (
              <>
                <div className="overflow-hidden rounded-xl border border-gray-200 mb-6">
                  <table className="w-full text-[13px]">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left py-3 px-4 font-bold text-gray-900">Spec</th>
                        <th className="text-right py-3 px-4 font-bold text-[#0469f0]">Fridge A</th>
                        <th className="text-right py-3 px-4 font-bold text-[#ff9951]">Fridge B</th>
                        <th className="text-center py-3 px-4 font-bold text-gray-900">Winner</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {results.comparison.map((row) => (
                        <tr key={row.feature}>
                          <td className="py-3 px-4 font-semibold text-gray-900">{row.feature}</td>
                          <td className={`py-3 px-4 text-right ${row.winner === "a" ? "font-bold text-[#0469f0]" : "text-gray-700"}`}>{row.a}</td>
                          <td className={`py-3 px-4 text-right ${row.winner === "b" ? "font-bold text-[#ff9951]" : "text-gray-700"}`}>{row.b}</td>
                          <td className="py-3 px-4 text-center">
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold text-white"
                              style={{
                                background:
                                  row.winner === "a" ? "#0469f0" : row.winner === "b" ? "#ff9951" : "#9ca3af",
                              }}
                            >
                              {row.winner === "tie" ? "Tie" : row.winner === "a" ? "A" : "B"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="grid grid-cols-2 border-y border-gray-300 divide-x divide-gray-300 mb-6">
                  <div className="py-5 pr-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">Overall winner</div>
                    <div
                      className="font-black"
                      style={{
                        fontSize: 30,
                        letterSpacing: "-0.03em",
                        color: results.winner === "a" ? "#0469f0" : results.winner === "b" ? "#ff9951" : "#374151",
                      }}
                    >
                      {results.winner === "tie" ? "Dead heat" : results.winner === "a" ? "Fridge A" : "Fridge B"}
                    </div>
                  </div>
                  <div className="py-5 pl-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">10-yr cost gap</div>
                    <div className="font-black text-gray-900" style={{ fontSize: 30, letterSpacing: "-0.03em" }}>
                      {`$${Math.round(results.diff).toLocaleString()}`}
                    </div>
                  </div>
                </div>

                <p className="text-[13px] text-gray-500 leading-[1.5]">
                  Score weights 10-yr total cost double — that&apos;s the line that captures both
                  upfront price and long-run energy. Capacity per dollar and headline kWh score
                  single weight.
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ============ SPEC FIGHT — narrative sides ============ */}
      <section className="bg-white py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="Side by side" title="Lower-price vs higher-efficiency" right="2 strategies" />

          <div className="grid md:grid-cols-2 gap-5">
            {[SIDE_BUDGET, SIDE_PREMIUM].map((s, i) => (
              <div
                key={s.name}
                className="rounded-xl p-6 md:p-7"
                style={{
                  border: `2px solid ${s.color}`,
                  background: i === 0 ? "rgba(4,105,240,0.03)" : "rgba(255,153,81,0.03)",
                }}
              >
                <div className="flex items-center justify-between mb-4 pb-4 border-b" style={{ borderColor: `${s.color}30` }}>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em]" style={{ color: s.color }}>
                      {i === 0 ? "Side A" : "Side B"}
                    </div>
                    <div className="text-[26px] font-black tracking-tight text-gray-900">{s.name}</div>
                    <div className="text-[12px] text-gray-500 mt-0.5">{s.tag}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-green-700 mb-2">✓ Pros</div>
                    <ul className="space-y-1.5 text-[13px] text-gray-700">
                      {s.pros.map((p) => <li key={p} className="leading-[1.4]">{p}</li>)}
                    </ul>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-500 mb-2">✕ Cons</div>
                    <ul className="space-y-1.5 text-[13px] text-gray-500">
                      {s.cons.map((c) => <li key={c} className="leading-[1.4]">{c}</li>)}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SCENARIOS ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[960px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="Which wins for you?" title="Six scenarios, six verdicts" />
          <div className="border-t border-gray-900">
            {SCENARIOS.map((s, i) => (
              <div
                key={s.who}
                className="grid grid-cols-[auto_1fr] md:grid-cols-[auto_1fr_auto] gap-3 md:gap-5 items-start md:items-center py-4 md:py-5 border-b border-gray-900"
              >
                <div className="font-mono text-[13px] font-bold min-w-[28px] text-[#ff9951] pt-1 md:pt-0">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div>
                  <h3 className="text-[15px] md:text-[16px] font-extrabold tracking-[-0.015em] text-gray-900">{s.who}</h3>
                  <p className="text-[13px] text-gray-600 mt-0.5 leading-[1.5]">{s.reason}</p>
                </div>
                <div className="col-start-2 md:col-auto md:text-right mt-2 md:mt-0">
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold text-white text-[12px]"
                    style={{ background: s.winner === "a" ? "#0469f0" : "#ff9951" }}
                  >
                    {s.winner === "a" ? "Lower-priced" : "Higher-efficiency"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PRIMARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Shop the category"
          title="Best French door refrigerators"
          description="The most popular full-size category — broad price and efficiency range to compare."
          collectionType="french-door"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-white py-8 md:py-12 border-t border-gray-200">
        <div className="max-w-[960px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="How we calculated" title="Methodology" />
          <div className="prose prose-sm max-w-none text-gray-700 space-y-3">
            <p>
              <strong>10-year horizon.</strong> ENERGY STAR uses a 12-year average refrigerator
              lifespan in savings calculations; we use 10 years for a conservative comparison.
              Adjust upward if your fridge typically lives longer.
            </p>
            <p>
              <strong>Energy cost.</strong> Annual cost = annual kWh × (rate ÷ 100). Default rate
              is 16.5¢/kWh — the recent US residential average per the{" "}
              <strong>US Energy Information Administration</strong>. Use your actual local rate
              from your utility bill for a real number.
            </p>
            <p>
              <strong>Annual kWh source.</strong> Required by FTC rule on the yellow EnergyGuide
              label of every full-size refrigerator. Also published in the ENERGY STAR product
              database and on most manufacturer spec pages.
            </p>
            <p>
              <strong>Scoring.</strong> Single weight on price, energy, capacity, cost-per-cu-ft,
              and 10-yr energy cost. Double weight on 10-yr total cost — the closest single number
              to &quot;what this fridge actually costs you.&quot;
            </p>
            <p>
              <strong>What&apos;s not modeled.</strong> Repair frequency, sealed-system warranty terms,
              sound rating, ice/water dispenser failure rates, and feature value. Treat those as
              separate decision inputs once cost is in range.
            </p>
          </div>
        </div>
      </section>

      {/* ============ TABBED SECONDARY CAROUSEL ============ */}
      <section className="bg-white py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="Deep dive" title="Best picks per format" />
          <div className="flex gap-2 mb-6 flex-wrap">
            {(
              [
                ["french", "French door"],
                ["counter", "Counter-depth"],
              ] as const
            ).map(([v, l]) => (
              <button
                key={v}
                type="button"
                onClick={() => setTab(v)}
                className={[
                  "px-5 py-2.5 rounded-full font-bold text-[12px] transition-all border border-gray-900",
                  tab === v ? "bg-gray-900 text-white" : "bg-white text-gray-900 hover:bg-gray-50",
                ].join(" ")}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <ToolProductCarousel
          key={tab}
          title={tab === "french" ? "Top French door picks" : "Top counter-depth picks"}
          collectionType={tab === "french" ? "french-door" : "counter-depth"}
          description={
            tab === "french"
              ? "Highest-volume category — broadest selection of efficient and budget options."
              : "Shallow profile that sits flush with kitchen cabinetry — usually a price premium per cu ft."
          }
        />
      </section>
    </ToolPageShell>
  )
}
